import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignLoadQuery, useCampaignUpdateMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignViewJson from "./campaign-orgunits-view.json";

export const CampaignOrgUnitsViewForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();

    const formDefinition = campaignViewJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignUpdateMutation();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Campaign" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignViewForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
